import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../core/i18n'
import { postVideoService } from '../../core/services'
import * as constants from '../../static/constants'
import * as interfaces from '../../static/interfaces'
import { Button, InputText, InputTextArea, InputRadio, InputLabel } from '../atoms'
import { Form } from '../molecules'

declare var window: interfaces.IWindow

const VIDEO_FIELDS = {
  name: 'name',
  description: 'description',
  media: 'media',
  youtube_video_id: 'youtube_video_id',
  image: 'image',
  position: 'position',
}

interface IErrors {
  [key: string]: string | null
}

interface IProps {
  post: interfaces.IPost
  postVideos: interfaces.IPostVideo[]
}

const videoInitialValues = {
  id: null,
  name: '',
  description: '',
  youtube_video_id: '',
  position: '',
}

const MEDIA_TYPE_OPTIONS = [
  {
    id: 1,
    name: 'YouTube',
    name_ja: 'YouTube',
    value: 'youtube',
  },
  {
    id: 2,
    name: 'Image',
    name_ja: '画像',
    value: 'image',
  },
]

const PostVideos: React.FC<IProps> = props => {
  const [editPostVideo, setEditPostVideo] = React.useState(null)
  const [postVideos, setPostVideos] = React.useState(props.postVideos || [])
  const [media, setMedia] = React.useState(editPostVideo?.media || 'youtube')
  const [image, setImage] = React.useState<any>(null)
  const [imageUrl, setImageUrl] = React.useState<any>(editPostVideo?.image_url || '')
  const [youtubeVideoId, setYoutubeVideoId] = React.useState<any>(
    editPostVideo?.youtube_video_id || null
  )
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false)
  const [errors, setErrors] = React.useState<IErrors>({})

  const fileRef = React.useRef(null)

  const handleSubmit = async (initialValues, values) => {
    const formData = new FormData()
    Object.keys(values).forEach(key => {
      if (key !== 'image') {
        formData.append(`post_video[${key}]`, values[key])
      }
    })

    formData.append(`post_video[post_id]`, `${props.post.id}`)

    if (editPostVideo.id) {
      formData.append(`post_video[id]`, editPostVideo.id)
    } else {
      formData.append(`post_video[position]`, values.position)
    }

    if (media === 'youtube') {
      formData.delete(`post_video[image]`)
    }

    if (image) {
      formData.append(`post_video[image]`, image)
    }
    try {
      const { flush } = editPostVideo.id
        ? await postVideoService.updatePostVideo(editPostVideo.id, formData)
        : await postVideoService.createPostVideo(formData)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      location.href = `/agent/posts/${props.post.slug}/edit?active=video`
    } catch (e) {
      console.error(e)
    }
  }

  const handleDelete = async id => {
    if (!confirm(`本当に削除しますか？`)) {
      return
    }
    await postVideoService.deletePostVideo(id)
    location.href = `/agent/posts/${props.post.slug}/edit?active=video`
  }

  const handleEdit = postVideo => {
    setEditPostVideo(postVideo)
  }

  const handleCreate = postVideo => {
    setEditPostVideo(postVideo)
  }

  const handleBackToIndex = () => {
    setEditPostVideo(null)
    setErrors({})
  }

  const handleUpdateForm = React.useCallback(
    (updatedErrors, updatedIsSubmitEnabled, values) => {
      setErrors(updatedErrors)
      if (
        !values?.name ||
        (values?.media === 'youtube' && !values?.youtube_video_id) ||
        (!editPostVideo.id && values?.media === 'image' && !values?.image)
      ) {
        setIsSubmitEnabled(false)
      } else {
        setIsSubmitEnabled(true)
      }
    },
    [editPostVideo, image]
  )

  const handleFileChange = e => {
    const file = e.target.files[0]
    if (file) {
      setImage(file)
      const reader = new FileReader()
      reader.onload = event => {
        setImageUrl(event.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  React.useEffect(() => {
    if (editPostVideo) {
      setMedia(editPostVideo.media || 'youtube')
      setImageUrl(editPostVideo.image_url || '')
      setYoutubeVideoId(editPostVideo.youtube_video_id || '')
    }
  }, [editPostVideo])

  return (
    <Wrapper>
      {editPostVideo && (
        <Form fields={VIDEO_FIELDS} handleUpdateForm={handleUpdateForm} handleSubmit={handleSubmit}>
          <div className="FormItmes">
            <ul>
              <FormItem>
                <InputRadio
                  options={MEDIA_TYPE_OPTIONS.map(data => {
                    return {
                      id: String(data.id),
                      label: `${I18n.locale === 'ja' ? data.name_ja : data.name}`,
                      value: data.value,
                    }
                  })}
                  defaultValue={editPostVideo?.media || 'youtube'}
                  name="media"
                  label={I18n.t('generic.media')}
                  onChangeHandler={e => setMedia(e.target.value)}
                />
              </FormItem>
              {media === 'youtube' ? (
                <>
                  <FormItem>
                    <InputText
                      required={media === 'youtube'}
                      name="youtube_video_id"
                      label="YouTube video id"
                      value={youtubeVideoId}
                      error={errors.youtube_video_id}
                      onChangeHandler={e => setYoutubeVideoId(e.target.value)}
                    />
                  </FormItem>
                </>
              ) : (
                <div className="ImageUpload" onClick={() => fileRef.current.click()}>
                  <InputLabel label={I18n.t('generic.image')} required={true} />
                  {imageUrl === '' ? (
                    <div className="image blank">Select image</div>
                  ) : (
                    <div className="image">
                      <img src={imageUrl} />
                    </div>
                  )}
                  <input type="hidden" name="youtube_video_id" value={youtubeVideoId} />
                </div>
              )}
              <input
                id="image"
                type="file"
                name="image"
                onChange={handleFileChange}
                ref={fileRef}
                accept="image/*"
              />
              <FormItem>
                <InputText
                  required={true}
                  name="name"
                  label="Name"
                  defaultValue={editPostVideo?.name}
                  error={errors.name}
                />
              </FormItem>
              <FormItem>
                <InputTextArea
                  name="description"
                  label="Description"
                  defaultValue={editPostVideo?.description}
                  error={errors.description}
                />
              </FormItem>
              <FormItem>
                <InputText
                  name="position"
                  label="Position"
                  defaultValue={editPostVideo?.position}
                  error={errors.position}
                />
              </FormItem>
            </ul>
          </div>
          <div className="Editor_Footer">
            <Button handleClick={() => handleBackToIndex()}>
              {I18n.t('generic.back_to_index')}
            </Button>
            <Button primary={true} disabled={!isSubmitEnabled}>
              {editPostVideo.id ? I18n.t('generic.update') : I18n.t('generic.create')}
            </Button>
          </div>
        </Form>
      )}

      {editPostVideo === null && (
        <VideoList>
          <ul>
            {postVideos.map(postVideo => (
              <li key={postVideo.id}>
                <div className="info">
                  {postVideo.media === 'youtube' ? (
                    <div className="video">
                      <iframe
                        key={postVideo.id}
                        width="240"
                        height="135"
                        src={`https://www.youtube.com/embed/${postVideo.youtube_video_id}`}
                        frameBorder="0"
                      />
                    </div>
                  ) : (
                    <div className="image">
                      <img src={postVideo.image_url} />
                    </div>
                  )}

                  <div className="name">{postVideo.name}</div>
                  <div className="location">{postVideo.description}</div>
                </div>
                <div>
                  <Button primary={true} handleClick={() => handleEdit({ ...postVideo })}>
                    {I18n.t('generic.edit')}
                  </Button>
                  <Button handleClick={() => handleDelete(postVideo.id)}>
                    {I18n.t('generic.delete')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          <div className="buttons">
            <Button primary={true} handleClick={() => handleCreate({ ...videoInitialValues })}>
              {I18n.t('generic.create_new')}
            </Button>
          </div>
        </VideoList>
      )}
    </Wrapper>
  )
}

const VideoList = styled.ul`
  li {
    padding: 16px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      flex-direction: column;
    }
  }

  li + li {
    border-top: solid 1px ${constants.COLORS.divider};
  }

  .info {
    max-width: 600px;
  }

  .name {
    font-weight: bold;
    margin-top: 12px;
  }

  .location {
    margin-top: 8px;
  }

  .buttons {
    margin-top: 24px;
  }

  .image {
    width: calc(33.33% - 32px);
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      width: calc(50% - 32px);
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      width: 100%;
    }
    img {
      object-fit: cover;
      width: 100%;
    }
  }
`

const Wrapper = styled.div`
  .Form {
    padding: 0;
  }

  .FormItmes {
    margin-top: 24px;
  }

  .map {
    height: 320px;
    margin-top: 32px;
  }

  .ImageUpload {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin: 16px 0;
    .image {
      width: calc(33.33% - 32px);
      color: #707c97;
      font-size: 14px;
      font-weight: bold;
      cursor: pointer;
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        width: calc(50% - 32px);
      }
      @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        width: 100%;
      }
      img {
        object-fit: cover;
        width: 100%;
      }
    }
    .image.blank {
      text-align: center;
      border: dashed 2px #bfc1c7;
      border-radius: 4px;
      height: 240px;
      line-height: 240px;
    }
  }
  #image {
    display: none;
  }
`

const FormItem = styled.li`
  & + & {
    margin-top: 16px;
  }

  .FormItem_Title {
    font-size: 15px;
    font-weight: bold;
  }
`

export default PostVideos
